// OwnerHQ inner panel: Economy editor — NX bundles, sub tier price, profit splits, boost rate, payout rate.
import { useEffect, useState } from "react";
import { Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function OwnerEconomyTab() {
  const [econ, setEcon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/owner/economy");
      setEcon(data);
    } catch { toast.error("Failed to load economy"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updateBundle = (i, patch) => setEcon((e) => ({
    ...e,
    nx_bundles: e.nx_bundles.map((b, idx) => idx === i ? { ...b, ...patch } : b),
  }));
  const removeBundle = (i) => setEcon((e) => ({ ...e, nx_bundles: e.nx_bundles.filter((_, idx) => idx !== i) }));
  const addBundle = () => setEcon((e) => ({ ...e, nx_bundles: [...e.nx_bundles, { nx: 100, price_aud: 1.20, label: "" }] }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        nx_bundles: econ.nx_bundles.map((b) => ({
          nx: Number(b.nx) || 0,
          price_aud: Number(b.price_aud) || 0,
          label: b.label || "",
        })),
        payout_aud_per_nx:  Number(econ.payout_aud_per_nx) || 0,
        promo_studio_free_until_iso: econ.promo_studio_free_until_iso,
      };
      const { data } = await api.put("/owner/economy", patch);
      setEcon(data);
      toast.success("Economy saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  if (loading || !econ) return <div className="text-white/40 text-sm">Loading economy…</div>;

  return (
    <div className="space-y-6" data-testid="owner-economy-tab">
      <div>
        <div className="label-eyebrow text-nexus-cyan">// economy · owner controls</div>
        <h3 className="font-display font-black text-2xl mt-1">Pricing, splits, promo.</h3>
        <p className="text-xs text-white/60 mt-1">Live for every signup and purchase the moment you save. Reach by Coins, Pricing, Wallet, Boost, and signup pages via <code className="text-nexus-cyan">GET /api/economy/public</code>.</p>
      </div>

      <section className="border border-white/10 bg-nexus-surface p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="label-eyebrow">// NX bundles</div>
          <button onClick={addBundle} className="btn-ghost text-[10px]" data-testid="economy-add-bundle"><Plus className="w-3 h-3" /> Add bundle</button>
        </div>
        <div className="space-y-2">
          {econ.nx_bundles.map((b, i) => (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-[100px_120px_1fr_auto] gap-2 items-center" data-testid={`bundle-row-${i}`}>
              <input value={b.nx} onChange={(e) => updateBundle(i, { nx: e.target.value })} type="number" min="0" placeholder="NX" className="min-w-0 bg-transparent border border-white/10 px-2 py-1.5 text-sm" />
              <input value={b.price_aud} onChange={(e) => updateBundle(i, { price_aud: e.target.value })} type="number" step="0.01" placeholder="AUD" className="min-w-0 bg-transparent border border-white/10 px-2 py-1.5 text-sm" />
              <input value={b.label} onChange={(e) => updateBundle(i, { label: e.target.value })} placeholder="Label (e.g. Starter)" className="min-w-0 bg-transparent border border-white/10 px-2 py-1.5 text-sm" />
              <button onClick={() => removeBundle(i)} className="text-white/40 hover:text-nexus-crimson justify-self-end"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </section>

      <section className="border border-white/10 bg-nexus-surface p-4 space-y-3">
        <div className="label-eyebrow">// payout</div>
        <Row label="Payout AUD per NX" testid="econ-payout-rate">
          <input value={econ.payout_aud_per_nx} onChange={(e) => setEcon({ ...econ, payout_aud_per_nx: e.target.value })} type="number" step="0.0001" className="bg-transparent border border-white/10 px-2 py-1.5 text-sm w-full sm:w-32" />
        </Row>
      </section>

      <section className="border border-white/10 bg-nexus-surface p-4 space-y-3">
        <div className="label-eyebrow">// promo · 1 month free studio tier</div>
        <Row label="Cutoff (ISO timestamp)" testid="econ-promo-cutoff">
          <input value={econ.promo_studio_free_until_iso || ""} onChange={(e) => setEcon({ ...econ, promo_studio_free_until_iso: e.target.value })} placeholder="2026-07-17T23:59:59+00:00" className="bg-transparent border border-white/10 px-2 py-1.5 text-sm w-full sm:w-72" />
        </Row>
        <p className="text-[11px] text-white/40">Anyone who signs up at or before this timestamp automatically gets 30 days of free Studio Tier access on first login.</p>
      </section>

      <button onClick={save} disabled={saving} className="btn-nexus" data-testid="economy-save">
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save economy"}
      </button>
    </div>
  );
}

function Row({ label, children, testid }) {
  // Stack label above the input on mobile instead of side-by-side -- a fixed
  // 200px label plus the input's own width added up to more than a narrow
  // phone screen has room for, pushing the input outside the card's edge.
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 text-sm" data-testid={testid}>
      <span className="text-white/60 sm:min-w-[200px]">{label}</span>
      {children}
    </div>
  );
}
